// ===========================================
// Public Footer Component
// ===========================================
// Footer for public-facing pages with school name, copyright, and legal links

import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

interface PublicFooterProps {
  schoolName?: string;
  schoolSlug?: string;
  /**
   * Admin event Preview: leave out "Cookie Settings", which would reset the ADMIN's own cookie consent.
   * Default OFF: every public page keeps it.
   */
  preview?: boolean;
}

export default function PublicFooter({ schoolName, preview = false }: PublicFooterProps) {
  const displayName = schoolName || "Music 'n Me";
  const year = new Date().getFullYear();
  const { resetConsent } = useCookieConsent();

  return (
    <Box
      component="footer"
      sx={{
        py: 3,
        mt: 4,
        borderTop: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            &copy; {year} {displayName}. All rights reserved.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <MuiLink
              component={Link}
              to="/legal/terms"
              variant="body2"
              color="text.secondary"
              underline="hover"
            >
              Terms of Service
            </MuiLink>
            <MuiLink
              component={Link}
              to="/legal/privacy"
              variant="body2"
              color="text.secondary"
              underline="hover"
            >
              Privacy Policy
            </MuiLink>
            <MuiLink
              component={Link}
              to="/legal/cookies"
              variant="body2"
              color="text.secondary"
              underline="hover"
            >
              Cookie Policy
            </MuiLink>
            {!preview && (
              <MuiLink
                component="button"
                onClick={(e) => {
                  e.preventDefault();
                  resetConsent();
                }}
                variant="body2"
                color="text.secondary"
                underline="hover"
                sx={{ cursor: 'pointer', border: 'none', background: 'none', p: 0 }}
              >
                Cookie Settings
              </MuiLink>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
